import React, { useRef, useState, useEffect } from 'react';
import { marked } from 'marked';
import TurndownService from 'turndown';
import { 
  FaBold, 
  FaItalic, 
  FaUnderline, 
  FaStrikethrough,
  FaAlignLeft, 
  FaAlignCenter, 
  FaAlignRight,
  FaListUl, 
  FaListOl,
  FaLink,
  FaCode,
  FaQuoteLeft
} from 'react-icons/fa';
import './HTMLEditor.css';
import { sanitizePastedHtml } from '../../utils/richText';

/**
 * HTMLEditor - A WYSIWYG editor for rich text content
 * Similar to GitHub's README editor with toolbar for formatting
 */
function HTMLEditor({ value, onChange, placeholder, disabled = false, readOnly = false }) {
  const editorRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const [internalValue, setInternalValue] = useState('');
  const [activeFormats, setActiveFormats] = useState({});
  const [currentHeading, setCurrentHeading] = useState('');
  const savedSelectionRef = useRef(null);
  const isApplyingCommandRef = useRef(false);
  const codeTypingModeRef = useRef(false);

  const turndownRef = useRef(null);

  useEffect(() => {
    // Configure once
    marked.setOptions({
      gfm: true,
      breaks: true
    });

    const service = new TurndownService({
      headingStyle: 'atx',
      bulletListMarker: '-',
      codeBlockStyle: 'fenced',
      emDelimiter: '*'
    });

    // Keep underline as HTML since Markdown doesn't support it
    service.addRule('underline', {
      filter: ['u'],
      replacement: function (content) {
        return content ? `<u>${content}</u>` : '';
      }
    });

    // Preserve strikethrough properly
    service.addRule('strikethrough', {
      filter: ['del', 's', 'strike'],
      replacement: function (content) {
        return content ? `~~${content}~~` : '';
      }
    });

    // Make blockquotes more predictable
    service.addRule('blockquote', {
      filter: 'blockquote',
      replacement: function (content) {
        const cleaned = content.replace(/^\n+|\n+$/g, '');
        return cleaned
          .split('\n')
          .map(line => (line.trim() ? `> ${line}` : '>'))
          .join('\n') + '\n\n';
      }
    });

    turndownRef.current = service;
  }, []);

  // Convert HTML to Markdown (robust)
  const htmlToMarkdown = (html) => {
    if (!html) return '';
    if (!turndownRef.current) return '';

    const normalized = html
      .replace(/<div><br\s*\/?>?<\/div>/gi, '<br>')
      .replace(/<div>/gi, '<p>')
      .replace(/<\/div>/gi, '</p>');

    const md = turndownRef.current.turndown(normalized);
    return (md || '').trim();
  };

  // Convert Markdown to HTML for display (robust)
  const markdownToHtml = (markdown) => {
    if (!markdown) return '';
    return marked.parse(markdown, {
      gfm: true,
      breaks: true,
      headerIds: false,
      mangle: false
    });
  };

  // Convert markdown to HTML for display
  useEffect(() => {
    if (editorRef.current && value !== undefined) {
      // Value from backend is already HTML, use it directly
      if (value !== internalValue) {
        // If the user is actively editing/applying a command, don't stomp the DOM.
        if (isApplyingCommandRef.current) return;

        // If not focused (or readOnly), safe to re-render the HTML.
        if (!isFocused || readOnly) {
          editorRef.current.innerHTML = value || '';
          setInternalValue(value);
          return;
        }

        // Focused editing: if parent forces a new value while focused, update but try to restore.
        const savedSelection = saveSelection();
        editorRef.current.innerHTML = value || '';
        setInternalValue(value);
        if (savedSelection) {
          requestAnimationFrame(() => restoreSelection(savedSelection));
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, isFocused, readOnly]);

  function handleKeyDown(e) {
    // If we're in code typing mode and user presses Space, exit code mode so they can continue normal text.
    if (codeTypingModeRef.current && e.key === ' ') {
      // Insert a normal space outside of <code> and move caret out.
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        let node = selection.getRangeAt(0).commonAncestorContainer;
        if (node.nodeType === Node.TEXT_NODE) node = node.parentNode;

        while (node && node !== editorRef.current) {
          if (node.nodeType === Node.ELEMENT_NODE && node.tagName?.toLowerCase() === 'code') {
            e.preventDefault();
            const spaceNode = document.createTextNode(' ');
            node.parentNode.insertBefore(spaceNode, node.nextSibling);

            const range = document.createRange();
            range.setStart(spaceNode, 1);
            range.setEnd(spaceNode, 1);
            selection.removeAllRanges();
            selection.addRange(range);

            codeTypingModeRef.current = false;

            requestAnimationFrame(() => {
              if (editorRef.current) {
                const htmlContent = normalizeHtmlForStorage(editorRef.current.innerHTML);
                setInternalValue(htmlContent);
                onChange(htmlContent);
                updateActiveFormats();
                updateCurrentHeading();
              }
            });
            return;
          }
          node = node.parentNode;
        }
      }
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      // Reset heading dropdown immediately when Enter is pressed
      // The new line will be a paragraph, not a heading
      setCurrentHeading('');
      
      // Check if we're inside a code element - if so, break out of it
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        let node = range.commonAncestorContainer;
        if (node.nodeType === Node.TEXT_NODE) node = node.parentNode;
        
        // Walk up to find code element
        while (node && node !== editorRef.current) {
          if (node.nodeType === Node.ELEMENT_NODE && node.tagName?.toLowerCase() === 'code') {
            // We're inside code - break out by inserting a paragraph after
            e.preventDefault();
            
            // Find the code element's parent
            const codeParent = node.parentNode;
            const codeIndex = Array.from(codeParent.childNodes).indexOf(node);
            
            // Create a new paragraph after the code element
            const newParagraph = document.createElement('p');
            newParagraph.innerHTML = '<br>';
            
            // Insert paragraph after code
            if (codeIndex < codeParent.childNodes.length - 1) {
              codeParent.insertBefore(newParagraph, codeParent.childNodes[codeIndex + 1]);
            } else {
              codeParent.appendChild(newParagraph);
            }
            
            // Move cursor to the new paragraph
            const newRange = document.createRange();
            newRange.setStart(newParagraph, 0);
            newRange.setEnd(newParagraph, 0);
            selection.removeAllRanges();
            selection.addRange(newRange);
            
            // Update content
            requestAnimationFrame(() => {
              if (editorRef.current) {
                const htmlContent = normalizeHtmlForStorage(editorRef.current.innerHTML);
                setInternalValue(htmlContent);
                onChange(htmlContent);
                updateActiveFormats();
                updateCurrentHeading();
              }
            });
            
            return;
          }
          node = node.parentNode;
        }
      }
      
      // Also update after a brief delay to ensure DOM has updated
      setTimeout(() => {
        updateCurrentHeading();
      }, 10);
    }
  }
  /**
   * Paste handler.
   *
   * The browser's default contentEditable paste keeps the source formatting but
   * drags along everything else: Word's mso-* declarations, Google Docs' inline
   * font stacks, colours and background shading from web pages. That is what
   * makes pasted text look wrong in the editor. Intercepting the paste and
   * running the clipboard HTML through the shared allowlist keeps the structure
   * the author wanted — headings, bold/italic, lists, links, tables — and drops
   * the source's typography so it inherits the editor's own.
   *
   * Plain-text-only clipboards fall through to the default behaviour, and
   * Shift+Ctrl+V (paste-as-plain-text) is honoured via the text/plain branch.
   */
  const handlePaste = (e) => {
    if (readOnly || disabled) return;

    const clipboard = e.clipboardData;
    if (!clipboard) return;

    const html = clipboard.getData('text/html');
    const text = clipboard.getData('text/plain');

    if (!html) {
      // No rich payload — let the browser insert the plain text itself.
      return;
    }

    e.preventDefault();

    const cleaned = sanitizePastedHtml(html);
    if (cleaned) {
      document.execCommand('insertHTML', false, cleaned);
    } else if (text) {
      document.execCommand('insertText', false, text);
    }

    requestAnimationFrame(() => {
      if (!editorRef.current) return;
      const htmlContent = normalizeHtmlForStorage(editorRef.current.innerHTML);
      setInternalValue(htmlContent);
      onChange(htmlContent);
      updateActiveFormats();
      updateCurrentHeading();
    });
  };

  const handleInput = (e) => {
    if (readOnly || disabled) return;

    let htmlContent = e.target.innerHTML;
    
    // Normalize and send to backend with proper structure preserved
    const normalizedContent = normalizeHtmlForStorage(htmlContent);
    setInternalValue(normalizedContent);
    onChange(normalizedContent);
    
    // Update active formats after input
    updateActiveFormats();
  };

  const normalizeHtmlForStorage = (html) => {
    if (!html) return '';

    // Create a temporary container to parse and manipulate the HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    // Process all child nodes
    const processNode = (node) => {
      if (node.nodeType === Node.ELEMENT_NODE) {
        // Standardize strike tags
        if (node.tagName.toLowerCase() === 'strike') {
          const del = document.createElement('del');
          del.innerHTML = node.innerHTML;
          // Copy style attribute if present
          if (node.hasAttribute('style')) {
            del.setAttribute('style', node.getAttribute('style'));
          }
          node.parentNode.replaceChild(del, node);
          return;
        }
        
        // Convert div to p while preserving style
        if (node.tagName.toLowerCase() === 'div') {
          const p = document.createElement('p');
          p.innerHTML = node.innerHTML;
          // Copy style attribute if present
          if (node.hasAttribute('style')) {
            p.setAttribute('style', node.getAttribute('style'));
          }
          node.parentNode.replaceChild(p, node);
          return;
        }
      }
    };

    // Walk through all nodes
    const walkNodes = (parent) => {
      const children = Array.from(parent.childNodes);
      children.forEach(child => {
        processNode(child);
        if (child.nodeType === Node.ELEMENT_NODE) {
          walkNodes(child);
        }
      });
    };

    walkNodes(tempDiv);

    // Get the processed HTML
    let out = tempDiv.innerHTML;

    // Clean up empty paragraphs (but preserve those with style attributes)
    out = out.replace(/<p(\s+[^>]*)?\s*>\s*<br\s*\/?>(?:\s*)<\/p>/gi, (match) => {
      // If the paragraph has a style attribute, keep it
      if (match.includes('style=')) {
        return match;
      }
      return '<br>';
    });

    return out;
  };

  const isElementTag = (node, tagName) => {
    return (
      node &&
      node.nodeType === Node.ELEMENT_NODE &&
      node.tagName &&
      node.tagName.toLowerCase() === tagName
    );
  };

  const closestEditableBlock = (fromNode) => {
    if (!editorRef.current) return null;
    let node = fromNode;
    if (!node) return null;

    // if it's a text node, use its parent
    if (node.nodeType === Node.TEXT_NODE) node = node.parentNode;

    while (node && node !== editorRef.current) {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const tag = node.tagName?.toLowerCase();
        if (['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'li', 'blockquote', 'pre'].includes(tag)) {
          return node;
        }
      }
      node = node.parentNode;
    }

    return editorRef.current;
  };

  const applyAlignment = (align) => {
    if (disabled || readOnly) return;
    if (!editorRef.current) return;

    editorRef.current.focus();

    // Save selection first
    const savedSelection = saveSelection();

    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    const range = selection.getRangeAt(0);
    let node = range.startContainer;

    // Find the closest block-level element containing the cursor
    let targetBlock = null;
    while (node && node !== editorRef.current) {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const tag = node.tagName?.toLowerCase();
        if (['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'pre', 'div', 'li'].includes(tag)) {
          targetBlock = node;
          break;
        }
      }
      node = node.parentNode;
    }

    if (targetBlock) {
      // Apply alignment only to the current block
      targetBlock.style.textAlign = align;
    } else {
      // No block element found - we're in inline content
      // Wrap the current line/selection in a paragraph with alignment
      try {
        const paragraph = document.createElement('p');
        paragraph.style.textAlign = align;
        
        // If there's a selection, wrap it
        if (!range.collapsed) {
          const contents = range.extractContents();
          paragraph.appendChild(contents);
          range.insertNode(paragraph);
          
          // Move cursor to end of paragraph
          range.selectNodeContents(paragraph);
          range.collapse(false);
          selection.removeAllRanges();
          selection.addRange(range);
        } else {
          // No selection - wrap the current line
          // Find all adjacent inline siblings
          let currentNode = range.startContainer;
          if (currentNode.nodeType === Node.TEXT_NODE) {
            currentNode = currentNode.parentNode;
          }
          
          // Get all siblings until we hit a block element
          const siblings = [];
          let sibling = currentNode;
          
          // Go backwards
          while (sibling && sibling.previousSibling) {
            sibling = sibling.previousSibling;
            if (sibling.nodeType === Node.ELEMENT_NODE && 
                ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'pre', 'div'].includes(sibling.tagName?.toLowerCase())) {
              break;
            }
            siblings.unshift(sibling);
          }
          
          // Add current node
          siblings.push(currentNode);
          
          // Go forwards
          sibling = currentNode;
          while (sibling && sibling.nextSibling) {
            sibling = sibling.nextSibling;
            if (sibling.nodeType === Node.ELEMENT_NODE && 
                ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'pre', 'div'].includes(sibling.tagName?.toLowerCase())) {
              break;
            }
            siblings.push(sibling);
          }
          
          // Wrap all siblings in a paragraph
          if (siblings.length > 0) {
            siblings.forEach(s => {
              paragraph.appendChild(s.cloneNode(true));
            });
            
            // Replace the first sibling with the paragraph
            if (siblings[0].parentNode) {
              siblings[0].parentNode.replaceChild(paragraph, siblings[0]);
            }
            
            // Remove the rest
            for (let i = 1; i < siblings.length; i++) {
              if (siblings[i].parentNode === editorRef.current) {
                editorRef.current.removeChild(siblings[i]);
              }
            }
          }
        }
      } catch (e) {
        console.error('Error applying alignment:', e);
      }
    }

    requestAnimationFrame(() => {
      // Restore selection
      if (savedSelection) {
        restoreSelection(savedSelection);
      }

      const htmlContent = normalizeHtmlForStorage(editorRef.current.innerHTML);
      setInternalValue(htmlContent);
      onChange(htmlContent);

      updateActiveFormats();
      updateCurrentHeading();
    });
  };

  // Track which formats are currently active at cursor position
  const updateActiveFormats = () => {
    if (!editorRef.current || readOnly || disabled) return;
    
    const selection = window.getSelection();
    let currentAlignment = null;
    let isInCode = false;
    
    // Check alignment and code state by walking up the DOM tree
    if (selection.rangeCount > 0) {
      let node = selection.getRangeAt(0).commonAncestorContainer;
      if (node.nodeType === Node.TEXT_NODE) {
        node = node.parentNode;
      }
      
      while (node && node !== editorRef.current) {
        if (node.nodeType === Node.ELEMENT_NODE) {
          const tag = node.tagName?.toLowerCase();
          
          // Check for code
          if (tag === 'code') {
            isInCode = true;
          }
          
          // Check for alignment on block elements
          if (!currentAlignment && ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'div', 'blockquote', 'li'].includes(tag)) {
            const computedStyle = window.getComputedStyle(node);
            const textAlign = node.style.textAlign || computedStyle.textAlign || '';
            if (textAlign && ['left', 'center', 'right', 'justify'].includes(textAlign)) {
              currentAlignment = textAlign;
            }
          }
        }
        node = node.parentNode;
      }
    }
    
    const formats = {
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      underline: document.queryCommandState('underline'),
      strikeThrough: document.queryCommandState('strikeThrough'),
      justifyLeft: currentAlignment === 'left',
      justifyCenter: currentAlignment === 'center',
      justifyRight: currentAlignment === 'right',
      insertUnorderedList: document.queryCommandState('insertUnorderedList'),
      insertOrderedList: document.queryCommandState('insertOrderedList'),
      code: isInCode
    };
    
    setActiveFormats(formats);
  };

  // Detect current heading level at cursor position
  const updateCurrentHeading = () => {
    if (!editorRef.current || readOnly || disabled) return;
    
    const selection = window.getSelection();
    if (selection.rangeCount === 0) {
      setCurrentHeading('');
      return;
    }
    
    const range = selection.getRangeAt(0);
    let node = range.commonAncestorContainer;
    
    // If it's a text node, get its parent
    if (node.nodeType === Node.TEXT_NODE) {
      node = node.parentNode;
    }
    
    // Walk up the DOM tree to find heading element
    while (node && node !== editorRef.current) {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const tagName = node.tagName?.toLowerCase();
        if (tagName && /^h[1-6]$/.test(tagName)) {
          const level = parseInt(tagName.charAt(1));
          setCurrentHeading(level.toString());
          return;
        }
        // If we hit a paragraph or other block element that's not a heading, it's normal text
        if (['p', 'div', 'blockquote', 'li'].includes(tagName)) {
          setCurrentHeading('');
          return;
        }
      }
      node = node.parentNode;
    }
    
    // If we reached the editor root without finding a heading, it's normal text
    setCurrentHeading('');
  };

  // Listen for selection changes to update active formats
  useEffect(() => {
    const handleSelectionChange = () => {
      if (isFocused && !readOnly && !disabled) {
        updateActiveFormats();
        updateCurrentHeading();
      }
    };

    document.addEventListener('selectionchange', handleSelectionChange);
    return () => document.removeEventListener('selectionchange', handleSelectionChange);
  }, [isFocused, readOnly, disabled]);

  const saveSelection = () => {
    if (!editorRef.current) return null;
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      // Clone the range to preserve it
      return {
  startContainer: range.startContainer,
  startOffset: range.startOffset,
  endContainer: range.endContainer,
  endOffset: range.endOffset
      };
    }
    return null;
  };

  const restoreSelection = (savedSelection) => {
    if (!savedSelection || !editorRef.current) return;
    
    try {
      const selection = window.getSelection();
      const range = document.createRange();
      
      // Check if the saved nodes are still in the document
      if (editorRef.current.contains(savedSelection.startContainer) &&
          editorRef.current.contains(savedSelection.endContainer)) {
        range.setStart(savedSelection.startContainer, savedSelection.startOffset);
        range.setEnd(savedSelection.endContainer, savedSelection.endOffset);
      } else {
        // If nodes vanished (e.g., after innerHTML rewrite), fall back to a safe caret.
        // Prefer end-of-editor rather than jumping to the start.
        range.selectNodeContents(editorRef.current);
        range.collapse(false);
      }
      
      selection.removeAllRanges();
      selection.addRange(range);
    } catch (e) {
      console.warn('Failed to restore selection:', e);
      // Fallback: place cursor at end
      const selection = window.getSelection();
      const range = document.createRange();
      range.selectNodeContents(editorRef.current);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  };

  const execCommand = (command, value = null) => {
    if (disabled || readOnly) return;

    // Keep the last editor selection so toolbar clicks can apply to it.
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      savedSelectionRef.current = saveSelection();
    }
    
    // Focus editor first - this is critical for execCommand to work
    if (editorRef.current) {
      editorRef.current.focus();
    }
    
    // Use requestAnimationFrame to ensure focus is set and DOM is ready
    requestAnimationFrame(() => {
      isApplyingCommandRef.current = true;

      // Restore selection if toolbar click caused selection loss
      const sel = window.getSelection();
      if (sel && sel.rangeCount === 0 && savedSelectionRef.current) {
        restoreSelection(savedSelectionRef.current);
      } else if (sel && sel.rangeCount === 0 && editorRef.current) {
        // No selection at all, create one at end
        const range = document.createRange();
        range.selectNodeContents(editorRef.current);
        range.collapse(false);
        sel.removeAllRanges();
        sel.addRange(range);
      }
      
      // Execute command (this will toggle if already applied)
      const success = document.execCommand(command, false, value);
      
      if (!success) {
        console.warn(`Command ${command} failed to execute`);
      }
      
      // Use another requestAnimationFrame to restore selection after DOM updates
      requestAnimationFrame(() => {
        // Persist selection for subsequent toolbar actions
        savedSelectionRef.current = saveSelection();
        
        // Update value and formats
        if (editorRef.current) {
          let htmlContent = editorRef.current.innerHTML;
          
          htmlContent = normalizeHtmlForStorage(htmlContent);
          
          // Send HTML directly to backend
          setInternalValue(htmlContent);
          onChange(htmlContent);
          updateActiveFormats();
          updateCurrentHeading();
        }

        // Allow parent-driven updates again
        isApplyingCommandRef.current = false;
      });
    });
  };

  const handleHeading = (level) => {
    if (disabled || readOnly) return;

    // Update heading state immediately for UI
    setCurrentHeading(level || '');

    // Use execCommand wrapper so selection doesn't jump and toggling works while typing
    if (!level || level === '') {
      execCommand('formatBlock', 'p');
    } else {
      execCommand('formatBlock', `h${level}`);
    }
  };

  const handleLink = () => {
    if (disabled || readOnly) return;
    
    const selection = window.getSelection();
    const selectedText = selection.toString();
    
    const url = prompt('Enter link URL:', 'https://');
    if (url) {
      if (selectedText) {
        // createLink doesn't add target/rel reliably; we'll fix it post-command.
        execCommand('createLink', url);
        requestAnimationFrame(() => {
          try {
            const sel2 = window.getSelection();
            if (!sel2 || sel2.rangeCount === 0) return;
            let node = sel2.getRangeAt(0).commonAncestorContainer;
            if (node.nodeType === Node.TEXT_NODE) node = node.parentNode;
            // walk up to find anchor
            while (node && node !== editorRef.current) {
              if (isElementTag(node, 'a')) {
                node.setAttribute('href', url);
                node.setAttribute('target', '_blank');
                node.setAttribute('rel', 'noopener noreferrer');
                break;
              }
              node = node.parentNode;
            }
            if (editorRef.current) {
              const htmlContent = normalizeHtmlForStorage(editorRef.current.innerHTML);
              setInternalValue(htmlContent);
              onChange(htmlContent);
            }
          } catch (err) {
            // no-op
          }
        });
      } else {
        const linkText = prompt('Enter link text:', url);
        if (linkText) {
          execCommand('insertHTML', `<a href="${url}" target="_blank" rel="noopener noreferrer">${linkText}</a>`);
        }
      }
    }
  };

  const handleBlockquote = () => {
    if (disabled || readOnly) return;
    
    execCommand('formatBlock', 'blockquote');
  };

  const handleCode = () => {
    if (disabled || readOnly) return;
    
    // Focus editor first
    if (editorRef.current) {
      editorRef.current.focus();
    }
    
    const selection = window.getSelection();
    if (!selection) return;

    if (selection.rangeCount === 0) {
      const range0 = document.createRange();
      range0.selectNodeContents(editorRef.current);
      range0.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range0);
    }

    const range = selection.getRangeAt(0);
    const selectedText = range.toString();

    // Check if cursor/selection is inside a code element
    let ancestor = range.commonAncestorContainer;
    if (ancestor.nodeType === Node.TEXT_NODE) ancestor = ancestor.parentNode;

    let existingCodeNode = null;
    let node = ancestor;
    while (node && node !== editorRef.current) {
      if (isElementTag(node, 'code')) {
        existingCodeNode = node;
        break;
      }
      node = node.parentNode;
    }

    if (existingCodeNode) {
      // Toggle OFF: unwrap the *specific* code node and move cursor right after it
      const textContent = existingCodeNode.textContent || '';
      const textNode = document.createTextNode(textContent);
      const parent = existingCodeNode.parentNode;
      parent.replaceChild(textNode, existingCodeNode);

      const newRange = document.createRange();
      newRange.setStart(textNode, textNode.textContent.length);
      newRange.setEnd(textNode, textNode.textContent.length);
      selection.removeAllRanges();
      selection.addRange(newRange);

      codeTypingModeRef.current = false;
    } else if (selectedText) {
      // Wrap selected text in <code>
      const codeElement = document.createElement('code');
      codeElement.textContent = selectedText;
      range.deleteContents();
      range.insertNode(codeElement);

      // Move cursor after the code element
      const newRange = document.createRange();
      newRange.setStartAfter(codeElement);
      newRange.setEndAfter(codeElement);
      selection.removeAllRanges();
      selection.addRange(newRange);

      codeTypingModeRef.current = false;
    } else {
      // No selection: toggle "code typing mode".
      // When enabled, we insert an empty <code>\u200B</code> and place caret inside it.
      // When disabled, we move caret outside and remove empty code wrappers.
      if (!codeTypingModeRef.current) {
        const codeElement = document.createElement('code');
        codeElement.textContent = '\u200B';

        range.insertNode(codeElement);

        const newRange = document.createRange();
        newRange.setStart(codeElement.firstChild, 1);
        newRange.setEnd(codeElement.firstChild, 1);
        selection.removeAllRanges();
        selection.addRange(newRange);
        codeTypingModeRef.current = true;
      } else {
        // Turn off: if caret is inside an empty code wrapper, remove it.
        const sel2 = window.getSelection();
        if (sel2 && sel2.rangeCount > 0) {
          let n = sel2.getRangeAt(0).commonAncestorContainer;
          if (n.nodeType === Node.TEXT_NODE) n = n.parentNode;
          while (n && n !== editorRef.current) {
            if (isElementTag(n, 'code')) {
              const txt = (n.textContent || '').replace(/\u200B/g, '');
              if (!txt.trim()) {
                const after = document.createTextNode('');
                n.parentNode.insertBefore(after, n.nextSibling);
                n.parentNode.removeChild(n);
                const r = document.createRange();
                r.setStart(after, 0);
                r.setEnd(after, 0);
                sel2.removeAllRanges();
                sel2.addRange(r);
              }
              break;
            }
            n = n.parentNode;
          }
        }

        codeTypingModeRef.current = false;
      }
    }

    requestAnimationFrame(() => {
      if (editorRef.current) {
        const htmlContent = normalizeHtmlForStorage(editorRef.current.innerHTML);
        setInternalValue(htmlContent);
        onChange(htmlContent);
        updateActiveFormats();
        updateCurrentHeading();
      }
    });
  };

  // If readOnly, render a simpler view
  if (readOnly) {
    return (
      <div className="htmlEditorWrapper readOnly">
        <div
          className="htmlEditorContent readOnlyContent"
          dangerouslySetInnerHTML={{ __html: value || '' }}
          onClick={(e) => {
            // Allow links to be clickable in read-only mode
            const target = e.target;
            if (target.tagName === 'A' && target.href) {
              // Open link in new tab
              window.open(target.href, '_blank', 'noopener,noreferrer');
            }
          }}
        />
      </div>
    );
  }

  return (
    <div className={`htmlEditorWrapper ${disabled ? 'disabled' : ''}`}>
      <div className="htmlToolbar">
        {/* Headings */}
        <div className="toolbarGroup">
          <select 
            className="headingSelect"
            onChange={(e) => {
              const level = e.target.value;
              handleHeading(level);
            }}
            value={currentHeading}
            disabled={disabled}
            title="Heading"
          >
            <option value="">Normal</option>
            <option value="1">Heading 1</option>
            <option value="2">Heading 2</option>
            <option value="3">Heading 3</option>
            <option value="4">Heading 4</option>
            <option value="5">Heading 5</option>
            <option value="6">Heading 6</option>
          </select>
        </div>

        <div className="toolbarSeparator" />

        {/* Text formatting */}
        <div className="toolbarGroup">
          <button 
            type="button" 
            onMouseDown={(e) => {
              e.preventDefault(); // Prevent focus loss
              execCommand('bold');
            }}
            onClick={(e) => {
              e.preventDefault(); // Prevent default button behavior
            }}
            title="Bold (Ctrl/Cmd + B)"
            className={`toolbarBtn ${activeFormats.bold ? 'active' : ''}`}
            disabled={disabled}
          >
            <FaBold />
          </button>
          <button 
            type="button" 
            onMouseDown={(e) => {
              e.preventDefault(); // Prevent focus loss
              execCommand('italic');
            }}
            onClick={(e) => {
              e.preventDefault(); // Prevent default button behavior
            }}
            title="Italic (Ctrl/Cmd + I)"
            className={`toolbarBtn ${activeFormats.italic ? 'active' : ''}`}
            disabled={disabled}
          >
            <FaItalic />
          </button>
          <button 
            type="button" 
            onMouseDown={(e) => {
              e.preventDefault(); // Prevent focus loss
              execCommand('underline');
            }}
            onClick={(e) => {
              e.preventDefault(); // Prevent default button behavior
            }}
            title="Underline (Ctrl/Cmd + U)"
            className={`toolbarBtn ${activeFormats.underline ? 'active' : ''}`}
            disabled={disabled}
          >
            <FaUnderline />
          </button>
          <button 
            type="button" 
            onMouseDown={(e) => {
              e.preventDefault(); // Prevent focus loss
              execCommand('strikeThrough');
            }}
            onClick={(e) => {
              e.preventDefault(); // Prevent default button behavior
            }}
            title="Strikethrough"
            className={`toolbarBtn ${activeFormats.strikeThrough ? 'active' : ''}`}
            disabled={disabled}
          >
            <FaStrikethrough />
          </button>
        </div>

        <div className="toolbarSeparator" />

        {/* Alignment */}
        <div className="toolbarGroup">
          <button 
            type="button" 
            onMouseDown={(e) => {
              e.preventDefault(); // Prevent focus loss
              applyAlignment('left');
            }}
            onClick={(e) => {
              e.preventDefault(); // Prevent default button behavior
            }}
            title="Align Left"
            className={`toolbarBtn ${activeFormats.justifyLeft ? 'active' : ''}`}
            disabled={disabled}
          >
            <FaAlignLeft />
          </button>
          <button 
            type="button" 
            onMouseDown={(e) => {
              e.preventDefault(); // Prevent focus loss
              applyAlignment('center');
            }}
            onClick={(e) => {
              e.preventDefault(); // Prevent default button behavior
            }}
            title="Align Center"
            className={`toolbarBtn ${activeFormats.justifyCenter ? 'active' : ''}`}
            disabled={disabled}
          >
            <FaAlignCenter />
          </button>
          <button 
            type="button" 
            onMouseDown={(e) => {
              e.preventDefault(); // Prevent focus loss
              applyAlignment('right');
            }}
            onClick={(e) => {
              e.preventDefault(); // Prevent default button behavior
            }}
            title="Align Right"
            className={`toolbarBtn ${activeFormats.justifyRight ? 'active' : ''}`}
            disabled={disabled}
          >
            <FaAlignRight />
          </button>
        </div>

        <div className="toolbarSeparator" />

        {/* Lists */}
        <div className="toolbarGroup">
          <button 
            type="button" 
            onMouseDown={(e) => {
              e.preventDefault(); // Prevent focus loss
              execCommand('insertUnorderedList');
            }}
            onClick={(e) => {
              e.preventDefault(); // Prevent default button behavior
            }}
            title="Bullet List"
            className={`toolbarBtn ${activeFormats.insertUnorderedList ? 'active' : ''}`}
            disabled={disabled}
          >
            <FaListUl />
          </button>
          <button 
            type="button" 
            onMouseDown={(e) => {
              e.preventDefault(); // Prevent focus loss
              execCommand('insertOrderedList');
            }}
            onClick={(e) => {
              e.preventDefault(); // Prevent default button behavior
            }}
            title="Numbered List"
            className={`toolbarBtn ${activeFormats.insertOrderedList ? 'active' : ''}`}
            disabled={disabled}
          >
            <FaListOl />
          </button>
        </div>

        <div className="toolbarSeparator" />

        {/* Additional formatting */}
        <div className="toolbarGroup">
          <button 
            type="button" 
            onMouseDown={(e) => {
              e.preventDefault(); // Prevent focus loss
            }}
            onClick={(e) => {
              e.preventDefault(); // Prevent default button behavior
              handleLink();
            }}
            title="Insert Link"
            className="toolbarBtn"
            disabled={disabled}
          >
            <FaLink />
          </button>
          <button 
            type="button" 
            onMouseDown={(e) => {
              e.preventDefault(); // Prevent focus loss
              handleCode();
            }}
            onClick={(e) => {
              e.preventDefault(); // Prevent default button behavior
            }}
            title="Inline Code"
            className={`toolbarBtn ${activeFormats.code ? 'active' : ''}`}
            disabled={disabled}
          >
            <FaCode />
          </button>
          <button 
            type="button" 
            onMouseDown={(e) => {
              e.preventDefault(); // Prevent focus loss
              handleBlockquote();
            }}
            onClick={(e) => {
              e.preventDefault(); // Prevent default button behavior
            }}
            title="Blockquote"
            className="toolbarBtn"
            disabled={disabled}
          >
            <FaQuoteLeft />
          </button>
        </div>
      </div>
      
      <div
        ref={editorRef}
        contentEditable={!disabled}
        className={`htmlEditorContent ${isFocused ? 'focused' : ''}`}
        onInput={handleInput}
        onPaste={handlePaste}
        onClick={(e) => {
          // In edit mode, allow links to be clickable (open in new tab)
          const target = e.target;
          if (target.tagName === 'A' && target.href) {
            // If Ctrl/Cmd is held, let default behavior work
            // Otherwise, open in new tab
            if (!e.ctrlKey && !e.metaKey) {
              e.preventDefault();
              window.open(target.href, '_blank', 'noopener,noreferrer');
            }
          }
        }}
        onFocus={() => {
          setIsFocused(true);
          updateActiveFormats();
          updateCurrentHeading();
        }}
        onBlur={() => setIsFocused(false)}
        onMouseUp={() => {
          savedSelectionRef.current = saveSelection();
          updateActiveFormats();
          updateCurrentHeading();
        }}
        onKeyUp={() => {
          savedSelectionRef.current = saveSelection();
          updateActiveFormats();
          updateCurrentHeading();
        }}
        onKeyDown={(e) => {
          handleKeyDown(e);
          savedSelectionRef.current = saveSelection();
        }}
        suppressContentEditableWarning
        data-placeholder={placeholder || 'Enter product description...'}
      />
    </div>
  );
}

export default HTMLEditor;
